import React, { useState } from 'react';
import { Plus, Trash, Pencil, Play } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const getTierClassName = (tier) => {
  if (tier === 'elite') return 'bg-[#D4AF37]/20 text-[#D4AF37]';
  if (tier === 'premium') return 'bg-white/10 text-white';
  return 'bg-zinc-800 text-zinc-400';
};

const OnDemandTab = ({ videos, courses, onCreateVideo, onUpdateVideo, onDeleteVideo }) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({ 
    course_id: '', 
    title: '', 
    description: '', 
    video_url: '',
    thumbnail_url: '',
    duration_minutes: '',
    original_air_date: '',
    tier_required: 'basic'
  });

  const resetForm = () => {
    setVideoForm({ 
      course_id: '', 
      title: '', 
      description: '', 
      video_url: '',
      thumbnail_url: '',
      duration_minutes: '',
      original_air_date: '',
      tier_required: 'basic'
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    await onCreateVideo({
      ...videoForm,
      duration_minutes: videoForm.duration_minutes ? parseInt(videoForm.duration_minutes) : null,
      original_air_date: videoForm.original_air_date ? new Date(videoForm.original_air_date).toISOString() : null
    });
    setIsCreateDialogOpen(false);
    resetForm();
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setVideoForm({
      course_id: video.course_id,
      title: video.title,
      description: video.description || '',
      video_url: video.video_url || '',
      thumbnail_url: video.thumbnail_url || '',
      duration_minutes: video.duration_minutes || '',
      original_air_date: video.original_air_date ? new Date(video.original_air_date).toISOString().slice(0, 10) : '',
      tier_required: video.tier_required || 'basic'
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    await onUpdateVideo(editingVideo.video_id, {
      ...videoForm,
      duration_minutes: videoForm.duration_minutes ? parseInt(videoForm.duration_minutes) : null,
      original_air_date: videoForm.original_air_date ? new Date(videoForm.original_air_date).toISOString() : null
    });
    setIsEditDialogOpen(false);
    setEditingVideo(null);
    resetForm();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const VideoFormFields = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label className="text-zinc-400">Course</Label>
        <Select 
          value={videoForm.course_id} 
          onValueChange={(value) => setVideoForm({ ...videoForm, course_id: value })}
        >
          <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
            <SelectValue placeholder="Select a course" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-700">
            {courses.map((course) => (
              <SelectItem key={course.course_id} value={course.course_id}>
                {course.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-zinc-400">Video Title</Label>
        <Input
          value={videoForm.title}
          onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
          className="bg-zinc-900 border-zinc-700 text-white"
          placeholder="e.g., Market Analysis - Week of Jan 6"
          required
        />
      </div>
      <div>
        <Label className="text-zinc-400">Description</Label>
        <Textarea
          value={videoForm.description}
          onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
          className="bg-zinc-900 border-zinc-700 text-white"
          placeholder="What was covered in this session?"
        />
      </div>
      <div>
        <Label className="text-zinc-400">Video URL (YouTube Embed)</Label>
        <Input
          value={videoForm.video_url}
          onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value })}
          className="bg-zinc-900 border-zinc-700 text-white"
          placeholder="https://www.youtube.com/embed/..."
          required
        />
        <p className="text-xs text-zinc-500 mt-1">Use YouTube embed URL format</p>
      </div>
      <div>
        <Label className="text-zinc-400">Thumbnail URL (optional)</Label>
        <Input
          value={videoForm.thumbnail_url}
          onChange={(e) => setVideoForm({ ...videoForm, thumbnail_url: e.target.value })}
          className="bg-zinc-900 border-zinc-700 text-white"
          placeholder="https://..."
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400">Duration (minutes)</Label>
          <Input
            type="number"
            value={videoForm.duration_minutes}
            onChange={(e) => setVideoForm({ ...videoForm, duration_minutes: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-white"
            placeholder="60"
          />
        </div>
        <div>
          <Label className="text-zinc-400">Original Air Date</Label>
          <Input
            type="date"
            value={videoForm.original_air_date}
            onChange={(e) => setVideoForm({ ...videoForm, original_air_date: e.target.value })}
            className="bg-zinc-900 border-zinc-700 text-white"
          />
        </div>
      </div>
      <div>
        <Label className="text-zinc-400">Required Tier</Label>
        <Select 
          value={videoForm.tier_required} 
          onValueChange={(value) => setVideoForm({ ...videoForm, tier_required: value })}
        >
          <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-700">
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
            <SelectItem value="elite">Elite</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158]">
        {submitLabel}
      </Button>
    </form>
  );

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl text-white font-outfit">On-Demand Videos</h2>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
              <Plus className="w-4 h-4 mr-2" />
              Add Recording
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Add On-Demand Video</DialogTitle>
            </DialogHeader>
            <VideoFormFields onSubmit={handleCreate} submitLabel="Add Video" />
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit On-Demand Video</DialogTitle>
          </DialogHeader>
          <VideoFormFields onSubmit={handleUpdate} submitLabel="Update Video" />
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map((video) => (
          <div key={video.video_id} className="bg-[#121212] border border-white/5 overflow-hidden">
            <div className="relative h-36 bg-zinc-900">
              {video.thumbnail_url ? (
                <img 
                  src={video.thumbnail_url} 
                  alt={video.title} 
                  className="w-full h-full object-cover" 
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Play className="w-12 h-12 text-zinc-700" weight="fill" />
                </div>
              )}
              {video.duration_minutes && (
                <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1">
                  {video.duration_minutes} min
                </span>
              )}
            </div>
            <div className="p-4">
              <h3 className="text-white font-medium line-clamp-1">{video.title}</h3>
              <p className="text-zinc-500 text-sm mb-2">{video.course_title}</p>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-zinc-500">
                  Aired: {formatDate(video.original_air_date)}
                </span>
                <span className={`text-xs px-2 py-1 ${getTierClassName(video.tier_required)}`}>
                  {video.tier_required}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(video)}
                  className="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteVideo(video.video_id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default OnDemandTab;
